import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProfileCompletionProvider, useProfileCompletion } from './contexts/ProfileCompletionContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { Toaster } from './components/ui/sonner';
import { ErrorBoundary } from './components/ErrorBoundary';
import axios from 'axios';
import LandingPage from './pages/LandingPage';
// All other routes are code-split so the first-paint bundle (marketing pages)
// stays small. Loading a route pulls in only its chunk on demand.
const AuthPage = lazy(() => import('./pages/AuthPage'));
const AuthCallback = lazy(() => import('./pages/AuthCallback'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const ProfileView = lazy(() => import('./pages/ProfileView'));
const Messaging = lazy(() => import('./pages/Messaging'));
const ResourceHub = lazy(() => import('./pages/ResourceHub'));
const Settings = lazy(() => import('./pages/Settings'));
const SubscriptionPlans = lazy(() => import('./pages/SubscriptionPlans'));
const Favorites = lazy(() => import('./pages/Favorites'));
const BrowseProviders = lazy(() => import('./pages/BrowseProviders'));
const EventsCalendar = lazy(() => import('./pages/EventsCalendar'));
const JobPostingView = lazy(() => import('./pages/JobPostingView'));
const ColoringPages = lazy(() => import('./pages/ColoringPages'));
const PDFResources = lazy(() => import('./pages/PDFResources'));
const PDFViewer = lazy(() => import('./pages/PDFViewer'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const ContactUs = lazy(() => import('./pages/ContactUs'));
const FAQ = lazy(() => import('./pages/FAQ'));
const PrivacyTerms = lazy(() => import('./pages/PrivacyTerms'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const BrandingMockup = lazy(() => import('./pages/BrandingMockup'));
const BusinessCards = lazy(() => import('./pages/BusinessCards'));
const GoogleCallback = lazy(() => import('./pages/GoogleCallback'));
const DailyReport = lazy(() => import('./pages/DailyReport'));
const MonthlyReport = lazy(() => import('./pages/MonthlyReport'));
const MonthlyReportSign = lazy(() => import('./pages/MonthlyReportSign'));
const FormSign = lazy(() => import('./pages/FormSign'));
const MyFormsInvoices = lazy(() => import('./pages/MyFormsInvoices'));
const MonthlyInvoice = lazy(() => import('./pages/MonthlyInvoice'));
import { LegalAgreementModal } from './components/LegalAgreementModal';
import TopNav from './components/TopNav';
import { OfflineBanner } from './components/OfflineBanner';
import { InstallPromptBanner } from './components/InstallPromptBanner';
import { AppFooter } from './components/AppFooter';
import { useAnalytics } from './hooks/useAnalytics';
import { captureAdAttribution } from './utils/adAttribution';

// Shared fallback while a route chunk downloads. Matches the ProtectedRoute
// loading spinner so page transitions feel consistent.
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="w-10 h-10 border-4 border-violet-200 border-t-violet-500 rounded-full animate-spin" />
  </div>
);

function ProtectedRoute({ children, hideNav }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-violet-200 border-t-violet-500 rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, needsAgreement, acceptAgreement } = useAuth();
  const { loading: pcLoading, complete: pcComplete } = useProfileCompletion();
  const [maintenance, setMaintenance] = useState(null);
  useAnalytics();

  // Capture ad-campaign attribution (utm_/fbclid/gclid) once on app mount so a landing
  // with campaign params logs a visit + snapshots first-touch for the eventual register.
  useEffect(() => { captureAdAttribution(); }, []);

  // Parent forced-completion gate: trap parents with incomplete profiles on /settings.
  useEffect(() => {
    const allowed = ['/settings', '/auth', '/', '/forgot-password', '/reset-password', '/auth/callback', '/auth/google-callback', '/privacy', '/about', '/contact', '/faq', '/verify-email'];
    if (user?.user_type === 'parent' && !pcLoading && !pcComplete && !allowed.includes(location.pathname)) {
      navigate('/settings', { replace: true });
    }
  }, [user, pcLoading, pcComplete, location.pathname, navigate]);

  // Set data-segment on <html> for global CSS theming
  useEffect(() => {
    const segment = user?.care_segment || 'childcare';
    document.documentElement.setAttribute('data-segment', segment);
    return () => document.documentElement.removeAttribute('data-segment');
  }, [user?.care_segment]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/system/status`).then(r => setMaintenance(r.data)).catch(() => setMaintenance({ maintenance: false }));
  }, []);

  // CRITICAL: Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <Suspense fallback={<RouteFallback />}><AuthCallback /></Suspense>;
  }

  // Show maintenance page for non-admin users
  if (maintenance?.maintenance && user?.user_type !== 'admin' && !['/auth', '/forgot-password', '/reset-password', '/auth/callback', '/'].includes(location.pathname)) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-amber-100 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-2.99L13.73 4.01c-.77-1.33-2.69-1.33-3.46 0L3.34 16.01C2.57 17.33 3.53 19 5.07 19z" /></svg>
          </div>
          <h1 className="text-2xl font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Fredoka' }}>Under Maintenance</h1>
          <p className="text-slate-500">{maintenance.message || "We're performing scheduled maintenance. Please check back shortly."}</p>
        </div>
      </div>
    );
  }

  // Pages where we hide the global nav (they have their own)
  const hideNavPages = ['/', '/auth', '/forgot-password', '/reset-password', '/auth/callback', '/browse', '/admin', '/dashboard', '/branding-mockup', '/business-cards'];
  const showGlobalNav = user && !hideNavPages.includes(location.pathname);
  // Routes that must lock the entire viewport (no page-level scroll). Fixes iOS Safari
  // reflow/zoom issues on the Messages page where tapping the input used to shift the
  // whole page, then the user could pull the top nav down and lose the message input.
  const isFixedViewportRoute = location.pathname === '/messages';

  const routes = (<>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<Suspense fallback={<RouteFallback />}><AuthPage /></Suspense>} />
      <Route path="/forgot-password" element={<Suspense fallback={<RouteFallback />}><ForgotPassword /></Suspense>} />
      <Route path="/reset-password" element={<Suspense fallback={<RouteFallback />}><ResetPassword /></Suspense>} />
      <Route path="/auth/callback" element={<Suspense fallback={<RouteFallback />}><AuthCallback /></Suspense>} />
      <Route path="/auth/google-callback" element={<Suspense fallback={<RouteFallback />}><GoogleCallback /></Suspense>} />
      <Route path="/browse" element={<Suspense fallback={<RouteFallback />}><BrowseProviders /></Suspense>} />
      <Route path="/dashboard" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><Dashboard /></Suspense></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><AdminDashboard /></Suspense></ProtectedRoute>} />
      <Route path="/daily-report" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><DailyReport /></Suspense></ProtectedRoute>} />
      <Route path="/monthly-report" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><MonthlyReport /></Suspense></ProtectedRoute>} />
      <Route path="/monthly-report/sign/:token" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><MonthlyReportSign /></Suspense></ProtectedRoute>} />
      <Route path="/sign/:token" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><FormSign /></Suspense></ProtectedRoute>} />
      <Route path="/my-forms" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><MyFormsInvoices /></Suspense></ProtectedRoute>} />
      <Route path="/invoice/:token" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><MonthlyInvoice /></Suspense></ProtectedRoute>} />
      <Route path="/search" element={<Suspense fallback={<RouteFallback />}><SearchResults /></Suspense>} />
      <Route path="/profile/:userId" element={<Suspense fallback={<RouteFallback />}><ProfileView /></Suspense>} />
      <Route path="/job-posting/:userId" element={<Suspense fallback={<RouteFallback />}><JobPostingView /></Suspense>} />
      <Route path="/messages" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><Messaging /></Suspense></ProtectedRoute>} />
      <Route path="/resources" element={<Suspense fallback={<RouteFallback />}><ResourceHub /></Suspense>} />
      <Route path="/coloring-pages" element={<Suspense fallback={<RouteFallback />}><ColoringPages /></Suspense>} />
      <Route path="/pdf-resources" element={<Suspense fallback={<RouteFallback />}><PDFResources /></Suspense>} />
      <Route path="/pdf-viewer/:filename" element={<Suspense fallback={<RouteFallback />}><PDFViewer /></Suspense>} />
      <Route path="/documentation" element={<Navigate to="/resources?tab=forms" replace />} />
      <Route path="/contact" element={<Suspense fallback={<RouteFallback />}><ContactUs /></Suspense>} />
      <Route path="/faq" element={<Suspense fallback={<RouteFallback />}><FAQ /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<RouteFallback />}><PrivacyTerms /></Suspense>} />
      <Route path="/about" element={<Suspense fallback={<RouteFallback />}><AboutPage /></Suspense>} />
      <Route path="/verify-email" element={<Suspense fallback={<RouteFallback />}><VerifyEmail /></Suspense>} />
      <Route path="/branding-mockup" element={<Suspense fallback={<RouteFallback />}><BrandingMockup /></Suspense>} />
      <Route path="/business-cards" element={<Suspense fallback={<RouteFallback />}><BusinessCards /></Suspense>} />
      <Route path="/favorites" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><Favorites /></Suspense></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><EventsCalendar /></Suspense></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><Settings /></Suspense></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><SubscriptionPlans /></Suspense></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><Suspense fallback={<RouteFallback />}><SubscriptionPlans /></Suspense></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
  </>);

  // Pages that render their own full-height layout (with their own X close button).
  // They lock the viewport (no page-level scroll) but STILL show the AppFooter at the
  // very bottom of the viewport so the About/Contact/FAQ/Privacy nav is one tap away.
  const legalPagePaths = new Set(['/about', '/contact', '/faq', '/privacy']);
  const isLegalPageRoute = legalPagePaths.has(location.pathname);

  const layoutBody = (
    <>
      {showGlobalNav && <TopNav />}
      {/* Global agreement modal — shown on any page when user needs to accept terms */}
      {user && needsAgreement && user.user_type !== 'admin' && (
        <LegalAgreementModal
          isOpen={true}
          onAgree={acceptAgreement}
          onCancel={() => {}}
          careSegment={user.care_segment || 'childcare'}
        />
      )}
      {isFixedViewportRoute ? (
        <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
          <Routes>{routes}</Routes>
        </div>
      ) : isLegalPageRoute ? (
        <>
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <Routes>{routes}</Routes>
          </div>
          <AppFooter />
        </>
      ) : (
        <><Routes>{routes}</Routes>{location.pathname !== '/messages' && <AppFooter />}</>
      )}
    </>
  );

  return (
    <>
      <OfflineBanner />
      {user && <InstallPromptBanner />}
      {isFixedViewportRoute || isLegalPageRoute ? (
        <div className="fixed inset-0 flex flex-col overflow-hidden bg-transparent" data-testid="fixed-viewport-root">
          {layoutBody}
        </div>
      ) : layoutBody}
    </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
            <ThemeProvider>
              <ProfileCompletionProvider>
                <AppRouter />
                <Toaster />
              </ProfileCompletionProvider>
            </ThemeProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
